import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Draggable from "react-draggable";
import { Modal, Row, Col, Button, Typography, Form } from "antd";
import { useDispatch } from "react-redux";
import { InputItem } from "../../../components/forms/FormItems";
import AutoCompleteFormItem, {
  SettingsDataSelect,
} from "../../../components/forms/AutoCompleteFormItem";
import { getPatientHospitalizations } from "../../../api/server";
import Moment from "moment";
import { extendMoment } from "moment-range";
import {
  commitChanges,
  loadProspectus,
  loadProspectuseslines,
} from "../prospectusSlice";
import { useSelector } from "react-redux";
import FormActions from "../../../components/forms/FormActions";
import CheckBoxItem from "../../../components/forms/CheckBoxItem";
import LineActionsTable from "./LineActionsTable";
import ChildrenAnimationWrapper from "../../../components/animations/ChildrenAnimationWrapper";
import AnimationChildWrapper from "../../../components/animations/AnimationChildWrapper";
import { CloseOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
const moment = extendMoment(Moment);

const { Title } = Typography;

const EditProspectusLine = (props) => {
  const { hide, visible, prospectusLine, fieldName } = props;
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const loading = useSelector(
    (state) => state.pendingActions["prospectus/update"]
  );
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const [disabled, setDisabled] = useState(true);
  const [refundReasons, setRefundReasons] = useState([]);
  const [hospitalizations, setHospitalizations] = useState([]);
  const [formValues, setFormValues] = useState({});
  const inputRef = useRef();

  useEffect(() => {
    if (prospectusLine) {
      form.setFieldsValue({
        ...prospectusLine,
        reclaimed_units:
          prospectusLine.reclaimed_units -
          prospectusLine.reported_reclaimed_units,
        reclaimed_hospitalization_minutes:
          Math.round(
            (prospectusLine.reclaimed_hospitalization_minutes * 100) / 60
          ) / 100,
        refund_amount:
          prospectusLine.refund_amount - prospectusLine.reported_refund_amount,
      });
    }
  }, [prospectusLine, fieldName, inputRef]);

  useEffect(() => {
    const fetchPatientHospitalizations = async () => {
      const data = await getPatientHospitalizations({
        patientId: prospectusLine.order.patient_id,
        scopePrefix,
      });
      let items = [];
      data?.data?.items?.forEach((item, i) => {
        const prospectusMonth = moment({
          year: prospectusLine.prospectus.year,
          month: prospectusLine.prospectus.month - 1,
          day: 1,
        });
        const range1 = moment.range(
          moment(prospectusMonth).startOf("month"),
          moment(prospectusMonth).endOf("month")
        );

        const range2 = moment.range(item.effective_from, item.effective_to);
        if (range1.overlaps(range2, { adjacent: true })) {
          items.push({
            ...item,
            label: `${moment(item.effective_from).format("DD/MM")} - ${moment(
              item.effective_to
            ).format("DD/MM")} (${item.hospitalization_institute.name})`,
          });
        }
      });

      setHospitalizations(items);
      if (prospectusLine.reclaimed_hospitalization_start_time) {
        const item = items.filter(
          (i) =>
            i.effective_from ===
            prospectusLine.reclaimed_hospitalization_start_time
        )[0];
        if (item) {
          form.setFieldValue("hospitalization", item.id);
          form.setFieldValue("attach_hospitalization", true);
          setFormValues({
            ...formValues,
            hospitalization: item.id,
            attach_hospitalization: true,
          });
        }
      }
    };
    if (fieldName === "reclaimed_units") {
      fetchPatientHospitalizations().catch(console.error);
    }
  }, [fieldName, prospectusLine]);

  const onValuesChange = (changedValues, allValues) => {
    setFormValues(allValues);
  };
  const onFinish = async (values) => {
    let final = { ...values };
    if (fieldName === "reclaimed_units") {
      final["reclaimed_units"] =
        values["reclaimed_units"] + prospectusLine.reported_reclaimed_units;
      final["reclaimed_hospitalization_minutes"] =
        values["reclaimed_hospitalization_minutes"] * 60;
      const filtered =
        values.attach_hospitalization &&
        hospitalizations.filter((i) => i.id === values.hospitalization)[0];
      final["reclaimed_hospitalization_institution_code"] = filtered
        ? filtered["hospitalization_institute"]["code"]
        : null;
      final["reclaimed_hospitalization_start_time"] = filtered
        ? filtered["effective_from"]
        : null;
      final["reclaimed_hospitalization_end_time"] = filtered
        ? filtered["effective_to"]
        : null;
    }
    if (fieldName === "refund_amount") {
      const selected = refundReasons.filter(
        (item) => item.id === values["refund_reason"]
      )[0];

      final["refund_amount"] =
        values["refund_amount"] + prospectusLine.reported_refund_amount;
      final["refund_reason"] = selected
        ? selected.name
        : values["refund_reason"];
    }
    const line = {
      lineId: prospectusLine.id,
      prospectusId: prospectusLine.prospectus_id,
      prospectusLine: { ...final },
    };

    const action = await dispatch(commitChanges(line));
    if (!action.error) {
      dispatch(loadProspectus());
      dispatch(loadProspectuseslines());
      setHospitalizations([]);
      setFormValues({});
      form.resetFields();
      hide();
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const onDecline = () => {
    setHospitalizations([]);
    setFormValues({});
    form.resetFields();
    hide();
  };
  const layout = {
    labelCol:
      fieldName === "refund_amount"
        ? { span: 3, offset: 3 }
        : fieldName === "reclaimed_units"
        ? { span: 6, offset: 3 }
        : { span: 6, offset: 6 },
    wrapperCol:
      fieldName === "refund_amount"
        ? { span: 13, offset: 1 }
        : fieldName === "reclaimed_units"
        ? { span: 11, offset: 1 }
        : { span: 5, offset: 1 },
  };

  const onMouseOver = () => {
    setDisabled(false);
  };

  const onMouseOut = () => {
    setDisabled(true);
  };

  const onRefundReasonChanged = (val) => {
    setRefundReasons(val);
  };

  return (
    <Modal
      modalRender={(modal) => (
        <Draggable disabled={disabled}>{modal}</Draggable>
      )}
      closable={false}
      maskClosable={false}
      open={visible}
      footer={null}
      onCancel={hide}
      title={null}
      width={"fit-content"}
      styles={{
        body: {
          padding: 0,
        },
      }}
      destroyOnClose={true}
    >
      <div>
        <div
          className="header"
          style={{ height: "40px" }}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          onFocus={() => {}}
          onBlur={() => {}}
        >
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onDecline}
          />
        </div>
        <Title
          style={{
            fontSize: "30px",
            fontWeight: 500,
            lineHeight: 1.27,
            color: "rgba(0, 0, 0, 0.85)",
            margin: "13px 0 40px 0 ",
            textAlign: "center",
          }}
        >
          <FormattedMessage id={`app.forms.prospectuses.title.${fieldName}`} />
        </Title>
        <Form
          {...layout}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
          form={form}
          style={{
            padding:
              fieldName === "reclaimed_units" ? "0 128px" : "0 128px 0px 128px",
            width: "850px",
          }}
        >
          <ChildrenAnimationWrapper>
            <Row>
              <Col span={24} style={{ padding: "0 20px" }}>
                <AnimationChildWrapper key={1} itemKey={1}>
                  <InputItem
                    name={fieldName}
                    category="prospectuses"
                    required={true}
                    number={true}
                    twoDigitsNumbers={true}
                    min={0}
                    autoFocus={true}
                  />
                </AnimationChildWrapper>

                {fieldName === "reclaimed_units" && (
                  <AnimationChildWrapper key={2} itemKey={2}>
                    <CheckBoxItem
                      showLabel={true}
                      name="attach_hospitalization"
                      category="prospectuses"
                    />
                  </AnimationChildWrapper>
                )}
                {fieldName === "reclaimed_units" &&
                  formValues.attach_hospitalization && (
                    <AnimationChildWrapper key={3} itemKey={3}>
                      <InputItem
                        name={"reclaimed_hospitalization_minutes"}
                        category="prospectuses"
                        number={true}
                        twoDigitsNumbers={true}
                        min={0}
                        max={formValues.reclaimed_units}
                      />
                    </AnimationChildWrapper>
                  )}
                {fieldName === "reclaimed_units" &&
                  formValues.attach_hospitalization && (
                    <AnimationChildWrapper key={4} itemKey={4}>
                      <AutoCompleteFormItem
                        name="hospitalization"
                        category="prospectuses"
                        required={true}
                        items={hospitalizations}
                        paramName={"label"}
                        getOptionLabel={(option) => option.label}
                      />
                    </AnimationChildWrapper>
                  )}
                {fieldName === "refund_amount" && (
                  <AnimationChildWrapper key={2} itemKey={2}>
                    <SettingsDataSelect
                      name="refund_reason"
                      category="prospectuses"
                      required={true}
                      settingsdataName={"system_tables/refund_reasons"}
                      onDataChanged={onRefundReasonChanged}
                    />
                  </AnimationChildWrapper>
                )}
                {fieldName === "refund_amount" && (
                  <AnimationChildWrapper key={3} itemKey={3}>
                    <InputItem
                      name={"refund_comment"}
                      category="prospectuses"
                    />
                  </AnimationChildWrapper>
                )}
              </Col>
              <Row style={{ width: "100%" }}>
                <Col span={24}>
                  <FormActions
                    resource="accounting"
                    action="editor"
                    onCancel={onDecline}
                    hideDelete={true}
                    hideDecline={true}
                    saveLoading={loading}
                    // disabled={!activeProspectus}
                  />
                </Col>
              </Row>
            </Row>
          </ChildrenAnimationWrapper>
        </Form>
        <LineActionsTable
          data={prospectusLine?.prospectus_line_actions || []}
        />
      </div>
    </Modal>
  );
};
EditProspectusLine.propTypes = {
  hide: PropTypes.func,
  addText: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default EditProspectusLine;
